import { MultiSelectCreatable, MultiSelectOption } from "@/components/misc/Multiselect";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI, { LiveEventForm } from "@/javascript/conduction-api";
import { Song } from "@/javascript/library-objects";
import { Instrument } from "@/javascript/music-objects";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, NumberInput, Skeleton, Stack, Text, TextInput } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext, useEffect, useState } from "react";
import ConductionModal from "../ConductionModal";

interface LiveEventCreatorProps {
	onClose: () => void;
	onCreated: () => void;
}

const LiveEventCreationForm: React.FC<LiveEventCreatorProps> = ({ onClose, onCreated }) => {
	const [instruments, setInstruments] = useState<Instrument[]>([]);
	const [loadingInstruments, setLoadingInstruments] = useState<boolean>(true);
	const [songs, setSongs] = useState<Song[]>([]);
	const [loadingSongs, setLoadingSongs] = useState<boolean>(true);

	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error("AdminLiveEventCreator must be a child of AuthContext.");
	}
	const token = auth.token;
	if (token === null) {
		return null;
	}

	// Set up the form with initial values and validations.
	const form = useForm({
		initialValues: {
			name: "",
			startTime: null,
			endTime: null,
			participantLimit: null,
			eventAdminLimit: null,
			instruments: [] as MultiSelectOption[],
			songs: [] as MultiSelectOption[],
		},
		validate: {
			name: (value) => (value.trim().length === 0 ? "LiveEvent name is required" : null),
			participantLimit: (value) =>
				value != null && value < 0 ? "Participant limit must not be negative" : null,
			eventAdminLimit: (value) =>
				value != null && value < 0 ? "Live event admin limit must not be negative" : null,
			startTime: (value: Date | null) =>
				value == null ? "Start time is required" : null,
			endTime: (value: Date | null) =>
				value == null ? "End time is required" : form.values.startTime != null && (value as Date).getTime() <= (form.values.startTime as Date).getTime() ? "End time must be after start time" : null,
		},
	});

	// Fetch instrument options once when the component mounts.
	useEffect(() => {
		const loadingInstruments = Utils.displayNotification(
			"Loading Instruments...",
			undefined,
			"blue",
			"loading"
		);
		const loadingSongs = Utils.displayNotification(
			"Loading Songs...",
			undefined,
			"blue",
			"loading"
		);

		const fetchInstruments = async () => {
			try {
				const instruments = await ConductionAPI.getInstruments();
				setInstruments(instruments || []);
				Utils.updateNotification(
					loadingInstruments,
					"Instruments Loaded",
					undefined,
					"green",
					<IconCheck size={16} />
				);
			} catch (error) {
				console.error("Error fetching instrument options", error);
				Utils.updateNotification(
					loadingInstruments,
					"Error Fetching Instruments",
					undefined,
					"red",
					<IconAlertCircle size={16} />,
					true
				);
			} finally {
				setLoadingInstruments(false);
			}
		};

		const fetchSongs = async () => {
			try {
				const songs = await ConductionAPI.getAllSongs(token);
				setSongs(songs || []);
				Utils.updateNotification(
					loadingSongs,
					"Songs Loaded",
					undefined,
					"green",
					<IconCheck size={16} />
				);
			} catch (error) {
				console.error("Error fetching instrument options", error);
				Utils.updateNotification(
					loadingSongs,
					"Error Fetching Songs",
					undefined,
					"red",
					<IconAlertCircle size={16} />,
					true
				);
			} finally {
				setLoadingSongs(false);
			}
		};

		fetchInstruments();
		fetchSongs();
	}, []);

	const handleSubmit = async () => {
		// Validate the form and display errors if necessary.
		if (!form.isValid()) {
			form.validate();
			return;
		}

		const notificationId = Utils.displayNotification(
			"Creating Live Event...",
			undefined,
			"blue",
			"loading"
		);

		const liveEventForm: LiveEventForm = {
			name: form.values.name,
			startTime: form.values.startTime,
			endTime: form.values.endTime,
			participantLimit: form.values.participantLimit == 0 ? null : form.values.participantLimit,
			eventAdminLimit: form.values.eventAdminLimit == 0 ? null : form.values.eventAdminLimit,
			instrumentIds: form.values.instruments.map((instr) =>
				instr.value.toString()
			),
			songIds: form.values.songs.map((song) =>
				song.value.toString()
			),
		};

		try {
			await ConductionAPI.createLiveEvent(liveEventForm, token);
			Utils.updateNotification(
				notificationId,
				"Live Event Created",
				undefined,
				"green",
				<IconCheck size={16} />
			);
			onCreated();
			onClose();
		} catch (error) {
			console.error("Error creating live event", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to create live event.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		}
	};

	const instrumentOptions: MultiSelectOption[] = instruments.map((instrument) => ({
		label: instrument.name,
		value: instrument.id,
	}));

	const songOptions: MultiSelectOption[] = songs.map((song) => ({
		label: `${song.songName} by ${song.artists.map(artist => artist.artistName).join(", ")}`,
		value: song.songId
	}))

	const height = 40; // Use 40vh for view height as in the AdminSongCreator

	return (
		<ConductionModal
			onClose={onClose}
			title="Create New Live Event"
			size="md"
		>
			<Box style={{ minHeight: `${height}vh` }}>
				{loadingInstruments || loadingSongs ? (
					<Skeleton height={"100%"} radius="md" />
				) : (
					<form onSubmit={form.onSubmit(handleSubmit)}>
						<Flex
							direction="column"
							justify="space-between"
							align="stretch"
							style={{ minHeight: `${height}vh` }}
							onKeyDown={(e) => e.stopPropagation()} // Prevent hotkey conflicts
							onKeyUp={(e) => e.stopPropagation()}
						>
							<Stack>
								<TextInput
									label="LiveEvent Name"
									{...form.getInputProps("name")}
									required
								/>

								<NumberInput
									label="Participant Limit"
									{...form.getInputProps("participantLimit")}
									required
								/>

								<NumberInput
									label="Event Admin Limit"
									{...form.getInputProps("eventAdminLimit")}
									required
								/>

								<DateTimePicker
									label="Start Time"
									{...form.getInputProps("startTime")}
								/>


								<DateTimePicker
									label="End Time"
									{...form.getInputProps("endTime")}
								/>

								<Stack>
									<Text fw={500}>Instruments</Text>
									<MultiSelectCreatable
										options={instrumentOptions}
										value={form.values.instruments}
										creatable={false}
										{...form.getInputProps("instruments")}
									/>
								</Stack>

								<Stack>
									<Text fw={500}>Songs</Text>
									<MultiSelectCreatable
										options={songOptions}
										value={form.values.songs}
										creatable={false}
										{...form.getInputProps("songs")}
									/>
								</Stack>
							</Stack>

							<Button
								type="submit"
								radius="xl"
								size="sm"
								loading={form.submitting}
								color="red"
							>
								Create Live Event
							</Button>
						</Flex>
					</form>
				)}
			</Box>
		</ConductionModal>
	);
};

export default LiveEventCreationForm;
